import { FunctionalComponent, h } from '@stencil/core';
import { Header } from './card-header';
import { DataPoints } from './card-data-points';
import { EsgDataPoints } from './card-esg-data-points';
import { RiskRating } from './card-risk-rating-data-point';

import { Colors, EsgVisibilityStyle } from '../../_types/fund-card-api';
import { FundData } from '../../_interfaces/fund-card/fund-data';
import { getDomicileType } from './_helpers';

interface CardContainerProps {
	data: FundData;
	appearance: string | boolean;
	animateCard: boolean;
	esgVisibility: EsgVisibilityStyle;
	Tooltip: string;
	Card: string;
	Button: string;
	AddToCompareButton: string;
	buyFundUrl?: string;
	buyButtonText?: string;
	buyButtonAriaLabel?: string;
	showBuyButton: boolean;
	onBuyButtonClick: () => void;
	showIsaReady?: boolean;
}

export const CardContainer: FunctionalComponent<CardContainerProps> = ({
	data,
	appearance,
	animateCard,
	esgVisibility,
	Tooltip,
	Card,
	Button,
	AddToCompareButton,
	buyButtonText = 'Buy',
	showBuyButton,
	onBuyButtonClick,
	showIsaReady,
}) => {
	const {
		riskIndicator,
		riskImages,
		buttonTranslations,
		domicile,
		domicileCode,
		showDataPoints,
		bgColor,
		fundUrl,
		fundId,
		sustainabilityLabel,
		showAddToCompare,
		locale,
		fundType,
		contentTranslations,
	} = data;
	const {
		compareBasketAddToCompare,
		compareBasketRemove,
		compareBasketView,
		compareBasketTooManyFundsTooltipHeader,
		compareBasketTooManyFundsTooltipBody,
	} = buttonTranslations;

	const addToCompareCanBeShown = () =>
		showAddToCompare && !(locale === 'en_US' && fundType === 'index');

	const buyButtonCanBeShown = () => showBuyButton && data?.ticker;

	let fundAppearance;
	if (
		appearance === 'block' ||
		appearance === 'primary' ||
		appearance === 'standard'
	)
		fundAppearance = 'standard';
	else if (appearance === 'secondary' || appearance === 'outlined')
		fundAppearance = 'outlined';

	const visibleButtons = [
		buyButtonCanBeShown(),
		addToCompareCanBeShown(),
	].filter(Boolean).length;

	const getButtonType = (
		button: 'buy' | 'addToCompare' | 'view',
		visibleButtons: number
	) => {
		if (fundAppearance === 'standard') {
			const buttonTypeMap: Record<number, Record<string, string | null>> = {
				0: { buy: null, addToCompare: null, view: null },
				1: showBuyButton
					? { buy: 'primary', addToCompare: null, view: 'secondary' }
					: { buy: null, addToCompare: 'primary', view: 'secondary' },
				2: { buy: 'primary', addToCompare: 'secondary', view: 'tertiary' },
			};
			return buttonTypeMap[visibleButtons]?.[button] || 'secondary';
		} else if (fundAppearance === 'outlined') {
			const buttonTypeMap: Record<number, Record<string, string | null>> = {
				0: { buy: null, addToCompare: null, view: null },
				1: showBuyButton
					? { buy: 'secondary', addToCompare: null, view: 'tertiary' }
					: { buy: null, addToCompare: 'secondary', view: 'tertiary' },
				2: { buy: 'secondary', addToCompare: 'tertiary', view: 'tertiary' },
				3: { buy: 'secondary', addToCompare: 'tertiary', view: 'tertiary' },
			};
			return buttonTypeMap[visibleButtons]?.[button] || 'secondary';
		}
		return 'secondary';
	};

	const buyButtonType = getButtonType('buy', visibleButtons);
	const addToCompareBtnType = getButtonType('addToCompare', visibleButtons);
	const viewBtnType = getButtonType('view', visibleButtons);

	const domicileType = getDomicileType(domicile, domicileCode);

	const viewButtonStyleProps = {
		compact: true,
		type: viewBtnType,
	};
	if (viewBtnType === 'tertiary') {
		viewButtonStyleProps['no-min-width'] = true;
	}

	return (
		<Card
			class={[
				`ds-appearance-${fundAppearance}`,
				data.inverted ? 'ds-inverted' : '',
				animateCard ? 'ds-animate-hover' : '',
			].join(' ')}
		>
			<Header
				domicileType={domicileType}
				data={data}
				showIsaReady={showIsaReady}
			/>

			{showDataPoints && data.activeDataPoints !== 'esg' ? (
				<DataPoints
					esgVisibility={esgVisibility}
					data={data}
					Tooltip={Tooltip}
				/>
			) : null}

			{riskIndicator && data.locale === 'nl_NL' ? (
				<RiskRating
					riskIndicator={riskIndicator}
					contentTranslations={contentTranslations}
					riskImages={riskImages}
					fundId={fundId}
				/>
			) : null}

			{showDataPoints && data.activeDataPoints === 'esg' ? (
				<div class="ds-card-container ds-card-data-container">
					<h4 class="ds-eyebrow-s ds-data-label">{sustainabilityLabel}</h4>
					<EsgDataPoints
						domicileType={domicileType}
						esgVisibility={esgVisibility}
						data={data}
						Tooltip={Tooltip}
						i={1}
					/>
				</div>
			) : null}

			<div class="ds-card-container ds-card-data-container ds-esg-data">
				{esgVisibility && esgVisibility !== 'none' && (
					<h4 class="ds-eyebrow-s ds-data-label">{sustainabilityLabel}</h4>
				)}
				<EsgDataPoints
					domicileType={domicileType}
					esgVisibility={esgVisibility}
					data={data}
					Tooltip={Tooltip}
				/>
			</div>

			{(addToCompareCanBeShown() || buyButtonCanBeShown()) && (
				<div class="ds-view-btn-container">
					{buyButtonCanBeShown() && (
						<Button
							color={bgColor as Colors}
							class="ds-button ds-buy-fund-button"
							{...viewButtonStyleProps}
							type={buyButtonType}
							data-link-event="CTA|buy-fund-modal"
							onClick={onBuyButtonClick}
						>
							{`${buttonTranslations.buyFundButtonText || buyButtonText} ${
								data.ticker || ''
							}`}
						</Button>
					)}
					{addToCompareCanBeShown() && (
						<AddToCompareButton
							color={bgColor as Colors}
							button-type={addToCompareBtnType}
							fund-id={fundId}
							add-to-compare-text={compareBasketAddToCompare}
							remove-from-compare-text={compareBasketRemove}
							too-many-funds-tooltip-header={
								compareBasketTooManyFundsTooltipHeader
							}
							too-many-funds-tooltip-body={compareBasketTooManyFundsTooltipBody}
						></AddToCompareButton>
					)}
					<Button
						class="ds-button ds-view-fund-button"
						{...viewButtonStyleProps}
						type={viewBtnType}
						href={fundUrl}
						is-link="true"
					>
						{compareBasketView}
					</Button>
				</div>
			)}
		</Card>
	);
};
